import { CompletionSource } from '@codemirror/autocomplete';
import { keymap } from '@codemirror/view';
import { Spacing } from '@persona-id/design-tokens';
import { PlayIcon } from '@persona-id/design-tokens/icons';
import { noop } from 'lodash/fp';
import { Language } from 'prism-react-renderer';
import { ReactNode, VFC } from 'react';
import styled from 'styled-components';

import CodeEditor from 'dashboard/components-lib/CodeEditor/CodeEditor';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import Button from 'design-system/components/Button';
import useToggle from 'lib/hooks/useToggle';

const Clickable = styled.div`
  cursor: pointer;
`;

const FloatButton = styled(Button)`
  position: absolute;
  top: ${Spacing[2]};
  right: ${Spacing[2]};
`;

const EditorContainer = styled.div<{
  fullscreen: boolean;
}>`
  position: relative;
  height: ${(props) => (props.fullscreen ? '500px' : '300px')};
`;

interface MaximizableCodeEditorProps {
  code: string;
  disableDebounce?: boolean;
  'data-test'?: string;
  isOutsideClickDisabled?: boolean;
  isReadOnly?: boolean;
  language: Language;
  maximized?: boolean;
  modalTitle: string;
  outsideExceptionalIdPrefixes?: string[];
  onClickRun?: (() => void) | false;
  onCodeUpdate: (code: string) => void;
  onClose?: () => void;
  renderAboveEditor?: () => ReactNode;
  renderBelowEditor?: () => ReactNode;
  // Completion sources for CodeMirrorEditor
  completionSources?: {
    javascript?: CompletionSource[];
    sql?: CompletionSource[];
  };
  offsetElementId?: string;
  fullscreenTop?: number;
}

const MaximizableCodeEditor: VFC<MaximizableCodeEditorProps> = ({
  code,
  completionSources,
  disableDebounce,
  'data-test': dataTest,
  isOutsideClickDisabled = false,
  isReadOnly = false,
  language,
  maximized = false,
  modalTitle,
  outsideExceptionalIdPrefixes = [],
  offsetElementId,
  fullscreenTop,
  onClickRun,
  onCodeUpdate,
  onClose,
  renderAboveEditor,
  renderBelowEditor,
}) => {
  const [isOpenedHoverPanel, { enable: openHoverPanel, disable: closeHoverPanel }] =
    useToggle(false);

  const onChange = (value: string) => {
    if (value !== code) {
      onCodeUpdate(value);
    }
  };

  return (
    <div data-test={dataTest}>
      <Clickable
        id='maximizable-code-editor__codeblock'
        role='figure'
        aria-label='Code preview'
        onClick={openHoverPanel}
      >
        <CodeHighlight language={language}>
          {code.split('\n').slice(0, 15).join('\n')}
        </CodeHighlight>
      </Clickable>
      {isOpenedHoverPanel && (
        <MaximizablePanel
          maximized={maximized}
          outsideExceptionalIdPrefixes={outsideExceptionalIdPrefixes}
          title={modalTitle}
          offsetElementId={offsetElementId}
          onOutsideClickAndEscPress={() => {
            if (!isOutsideClickDisabled) {
              closeHoverPanel();
              if (onClose) {
                onClose();
              }
            }
          }}
          onClosePanel={() => {
            closeHoverPanel();
            if (onClose) {
              onClose();
            }
          }}
          fullscreenTop={fullscreenTop}
        >
          {(fullScreen: boolean) => (
            <>
              {renderAboveEditor && renderAboveEditor()}
              <EditorContainer fullscreen={fullScreen}>
                <CodeEditor
                  code={code}
                  disableDebounce={disableDebounce}
                  extensions={
                    onClickRun
                      ? [
                          keymap.of([
                            {
                              key: 'Mod-Enter',
                              run: () => {
                                onClickRun();
                                return true;
                              },
                            },
                          ]),
                        ]
                      : []
                  }
                  completionSources={completionSources}
                  language={language}
                  readOnly={isReadOnly}
                  onChange={onChange}
                />
                {onClickRun != null && (
                  <FloatButton
                    disabled={onClickRun === false}
                    id='maximizable-code-editor__button-run'
                    prefixIcon={<PlayIcon />}
                    type='button'
                    variant='basic'
                    width='auto'
                    onClick={onClickRun !== false ? onClickRun : noop}
                  >
                    Run
                  </FloatButton>
                )}
              </EditorContainer>
              {renderBelowEditor && renderBelowEditor()}
            </>
          )}
        </MaximizablePanel>
      )}
    </div>
  );
};

export default MaximizableCodeEditor;
